
// Enhanced security utilities with proper CSP and security headers

export const generateCSPHeader = (): string => {
  const nonce = generateNonce();
  
  return [
    "default-src 'self'",
    `script-src 'self' 'nonce-${nonce}' 'strict-dynamic' https://www.googletagmanager.com https://www.google-analytics.com https://pagead2.googlesyndication.com https://partner.googleadservices.com https://tpc.googlesyndication.com`,
    "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://pagead2.googlesyndication.com",
    "font-src 'self' https://fonts.gstatic.com data:",
    "img-src 'self' data: blob: https: http:",
    "media-src 'self' data: blob: https:",
    "object-src 'none'",
    "base-uri 'self'",
    "form-action 'self'",
    "manifest-src 'self'",
    "worker-src 'self' blob:",
    "child-src 'self' blob: https://www.youtube.com https://player.vimeo.com",
    "frame-src 'self' https://www.youtube.com https://player.vimeo.com https://pagead2.googlesyndication.com https://googleads.g.doubleclick.net",
    "connect-src 'self' https://*.supabase.co wss://*.supabase.co https://www.google-analytics.com https://region1.google-analytics.com https://pagead2.googlesyndication.com https://googleads.g.doubleclick.net",
    "upgrade-insecure-requests",
    "block-all-mixed-content"
  ].join('; ');
};

export const generateHSTSHeader = (): string => {
  return "max-age=63072000; includeSubDomains; preload";
};

export const getCOOPHeader = (): string => {
  return "same-origin";
};

export const generateNonce = (): string => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

export const initializeSecurityHeaders = () => {
  if (typeof window === 'undefined') return;
  
  // Set up CSP via meta tag (limited functionality)
  const existingCSP = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
  if (!existingCSP) {
    const metaCSP = document.createElement('meta');
    metaCSP.httpEquiv = 'Content-Security-Policy';
    metaCSP.content = generateCSPHeader();
    document.head.appendChild(metaCSP);
  }
  
  // Add security-related meta tags
  const securityMetas = [
    { httpEquiv: 'X-Content-Type-Options', content: 'nosniff' },
    { httpEquiv: 'X-XSS-Protection', content: '1; mode=block' },
    { httpEquiv: 'Cross-Origin-Opener-Policy', content: getCOOPHeader() },
    { httpEquiv: 'Cross-Origin-Embedder-Policy', content: 'require-corp' },
    { httpEquiv: 'Cross-Origin-Resource-Policy', content: 'cross-origin' },
    { httpEquiv: 'Referrer-Policy', content: 'strict-origin-when-cross-origin' },
    { httpEquiv: 'Permissions-Policy', content: 'camera=(), microphone=(), geolocation=(), interest-cohort=()' }
  ];
  
  securityMetas.forEach(({ httpEquiv, content }) => {
    const existing = document.querySelector(`meta[http-equiv="${httpEquiv}"]`);
    if (!existing) {
      const meta = document.createElement('meta');
      meta.httpEquiv = httpEquiv;
      meta.content = content;
      document.head.appendChild(meta);
    }
  });
};

export const preventClickjacking = () => {
  if (typeof window === 'undefined') return;
  
  // Check if the page is being loaded in a frame
  if (window.top !== window.self) {
    // If it's an admin page or sensitive page, block it
    const currentPath = window.location.pathname;
    const adminPaths = ['/admin', '/portal'];
    
    // Check if we're in Lovable's development environment
    const isLovableEnv = window.location.hostname.includes('lovableproject.com');
    
    if (adminPaths.some(path => currentPath.toLowerCase().startsWith(path.toLowerCase()))) {
      if (isLovableEnv) {
        // Just silently allow for Lovable development environment
        return;
      }
      throw new Error('This page cannot be displayed in a frame for security reasons.');
    }
  }
};

export const shouldAllowIndexing = (pathname: string = ''): boolean => {
  const adminPaths = ['/admin', '/portal'];
  return !adminPaths.some(path => pathname.toLowerCase().startsWith(path.toLowerCase()));
};

// Input sanitization function
export const sanitizeInput = (input: string, maxLength: number = 1000): string => {
  if (!input || typeof input !== 'string') return '';
  
  // Remove dangerous characters and limit length
  return input
    .replace(/[<>'"&]/g, '') // Remove HTML/script injection characters
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/data:/gi, '') // Remove data: protocol
    .replace(/vbscript:/gi, '') // Remove vbscript: protocol
    .trim()
    .substring(0, maxLength);
};

// SQL injection detection
export const detectSQLInjection = (input: string): boolean => {
  if (!input || typeof input !== 'string') return false;
  
  const sqlPatterns = [
    /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION|SCRIPT)\b)/gi,
    /(\-\-|\#|\/\*|\*\/)/gi, // SQL comments
    /(\bOR\b.*=.*\bOR\b)/gi, // OR-based injections
    /(\bAND\b.*=.*\bAND\b)/gi, // AND-based injections
    /([\'\"];?\s*(OR|AND)\s*[\'\"]?\d+[\'\"]?\s*=\s*[\'\"]?\d+)/gi, // 1=1 style
    /(\bUNION\b.*\bSELECT\b)/gi, // UNION SELECT
    /(\bEXEC\b.*\()/gi // EXEC statements
  ];
  
  return sqlPatterns.some(pattern => pattern.test(input));
};

// Email validation
export const validateEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') return false;
  
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email) && email.length <= 254; // RFC 5321 limit
};

// IP address validation (IPv4 and IPv6)
export const validateIPAddress = (ip: string): boolean => {
  if (!ip || typeof ip !== 'string') return false;
  
  // IPv4 validation
  const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  
  // IPv6 validation (simplified)
  const ipv6Regex = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^::1$|^::$/;
  
  return ipv4Regex.test(ip) || ipv6Regex.test(ip);
};

// Rate limiter implementation
interface RateLimitEntry {
  count: number;
  resetTime: number;
}

class RateLimiter {
  private store: Map<string, RateLimitEntry> = new Map();
  
  isAllowed(key: string, maxAttempts: number, windowMs: number): boolean {
    const now = Date.now();
    const entry = this.store.get(key);
    
    if (!entry || now >= entry.resetTime) {
      this.store.set(key, {
        count: 1,
        resetTime: now + windowMs
      });
      return true;
    }
    
    if (entry.count >= maxAttempts) {
      return false;
    }
    
    entry.count++;
    return true;
  }
  
  reset(key: string): void {
    this.store.delete(key);
  }
  
  cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.store.entries()) {
      if (now >= entry.resetTime) {
        this.store.delete(key);
      }
    }
  }
}

export const rateLimiter = new RateLimiter();

// Clean up expired entries every 5 minutes
if (typeof window !== 'undefined') {
  setInterval(() => {
    rateLimiter.cleanup();
  }, 5 * 60 * 1000);
}
